import { useContext } from 'react';
import { ImperativeContext, type CanvasImperative } from './imperative-context';

/**
 * Read the canvas imperative API from any descendant of
 * <ImperativeProvider>. Returns null when:
 *  - no provider ancestor (e.g. component mounted in isolation), OR
 *  - the bridge component isn't mounted yet (RF not ready)
 *
 * Consumers must null-check before calling. Pattern:
 *
 *     const canvas = useImperative();
 *     const handleClick = () => {
 *       if (!canvas) return;
 *       canvas.setCenter(x, y);
 *     };
 *
 * The hook reads the ref's current value at call time — it does NOT
 * subscribe to changes. That's intentional: imperative methods don't
 * need re-render reactivity, and reading via ref avoids spurious
 * renders when the bridge mounts/remounts. Components that need to
 * react to "the bridge is ready / not ready" should pair this with
 * their own state for that signal.
 */
export function useImperative(): CanvasImperative | null {
  const ref = useContext(ImperativeContext);
  return ref?.current ?? null;
}
